import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["alignment", "children", "distribution", "orientation", "spacing"];

/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { getChildrenElements, wrapElementWithComponent } from '@flowio/react-helpers';
import React from 'react';
import { StackAlignment } from '../constants/StackAlignment';
import { StackDistribution } from '../constants/StackDistribution';
import { StackOrientation } from '../constants/StackOrientation';
import { StackSpacing } from '../constants/StackSpacing';
import StackItem from './StackItem';

var getAlignItems = function getAlignItems(alignment) {
  switch (alignment) {
    case StackAlignment.Baseline:
      return 'baseline';

    case StackAlignment.Center:
      return 'center';

    case StackAlignment.Fill:
      return 'stretch';

    case StackAlignment.Leading:
      return 'flex-start';

    case StackAlignment.Trailing:
      return 'flex-end';

    default:
      return undefined;
  }
};

var getJustifyContent = function getJustifyContent(distribution) {
  switch (distribution) {
    case StackDistribution.EqualSpacing:
      return 'space-between';

    case StackDistribution.EqualCentering:
      return 'space-evenly';

    default:
      return undefined;
  }
};

var getStackItemFlex = function getStackItemFlex(distribution) {
  switch (distribution) {
    case StackDistribution.EqualCentering:
    case StackDistribution.EqualSpacing:
      return '0 1 auto';

    case StackDistribution.Fill:
    case StackDistribution.FillProportionally:
      return '1 1 auto';

    case StackDistribution.FillEqually:
      return '1 0 0%';

    default:
      return undefined;
  }
};

var getStackItemMinWidth = function getStackItemMinWidth(distribution) {
  switch (distribution) {
    case StackDistribution.EqualCentering:
    case StackDistribution.EqualSpacing:
      return '0';

    case StackDistribution.FillProportionally:
    case StackDistribution.FillEqually:
      return 'fit-content';

    default:
      return undefined;
  }
};

var getMarginMultiplier = function getMarginMultiplier(spacing) {
  switch (spacing) {
    case StackSpacing.ExtraTight:
      return 0.25;

    case StackSpacing.Tight:
      return 0.5;

    case StackSpacing.Loose:
      return 0.75;

    case StackSpacing.ExtraLoose:
      return 1;

    default:
      return 0;
  }
};

var Stack = function Stack(_ref) {
  var _ref$alignment = _ref.alignment,
      alignment = _ref$alignment === void 0 ? StackAlignment.Fill : _ref$alignment,
      children = _ref.children,
      _ref$distribution = _ref.distribution,
      distribution = _ref$distribution === void 0 ? StackDistribution.Fill : _ref$distribution,
      _ref$orientation = _ref.orientation,
      orientation = _ref$orientation === void 0 ? StackOrientation.Horizontal : _ref$orientation,
      _ref$spacing = _ref.spacing,
      spacing = _ref$spacing === void 0 ? StackSpacing.None : _ref$spacing,
      unhandledProps = _objectWithoutProperties(_ref, _excluded);

  var rootStyles = function rootStyles(theme) {
    return /*#__PURE__*/css({
      display: 'flex',
      flexWrap: 'nowrap',
      flexDirection: orientation === StackOrientation.Vertical ? 'column' : 'row',
      alignItems: getAlignItems(alignment),
      justifyContent: getJustifyContent(distribution),
      marginTop: "-".concat(getMarginMultiplier(spacing), "rem"),
      marginLeft: theme.direction === 'ltr' ? "-".concat(getMarginMultiplier(spacing), "rem") : undefined,
      marginRight: theme.direction === 'rtl' ? "-".concat(getMarginMultiplier(spacing), "rem") : undefined,
      marginInlineStart: "-".concat(getMarginMultiplier(spacing), "rem"),
      '& > div': {
        flex: getStackItemFlex(distribution),
        minWidth: getStackItemMinWidth(distribution),
        marginTop: "".concat(getMarginMultiplier(spacing), "rem"),
        marginLeft: theme.direction === 'ltr' ? "".concat(getMarginMultiplier(spacing), "rem") : undefined,
        marginRight: theme.direction === 'rtl' ? "".concat(getMarginMultiplier(spacing), "rem") : undefined,
        marginInlineStart: "".concat(getMarginMultiplier(spacing), "rem"),
        maxWidth: "calc(100% - ".concat(getMarginMultiplier(spacing), "rem)")
      }
    }, process.env.NODE_ENV === "production" ? "" : ";label:rootStyles;", process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
  };

  return jsx("div", _extends({}, unhandledProps, {
    css: rootStyles
  }), getChildrenElements(children).map(function (element, index) {
    return wrapElementWithComponent(element, StackItem, {
      key: index
    });
  }));
};

export default Stack;